import * as React from "react";
import CNCode from "../../../../Models/Interfaces/CNCode";
import "./CNCodeTreeSelect.css";

interface CNCodeTreeSelectProps {
    options: CNCode[];
    value?: string | null;
    onChange?: (cnCode: string | null, description?: string) => void;
    disabled?: boolean;
    disabledText?: string;
}

// -------------------- helpers --------------------
type TreeNode = CNCode & {
    cnCode: string | null;
    description?: string;
    allowedSelection?: boolean;
    children: TreeNode[];
    _pathKey: string;
};

function normalizePath(path: CNCode["path"]): string[] {
    if (Array.isArray(path)) return path.filter(Boolean);
    if (typeof path === "string") return path.split("/").filter(Boolean);
    return [];
}

function buildCnBreadcrumbs(options: CNCode[], selectedCnCode: string | null) {
    if (!selectedCnCode) return [];

    const selectedObj = options.find(o => o.cnCode === selectedCnCode);
    if (!selectedObj) return [];

    const segs = normalizePath(selectedObj.path);
    if (segs.length === 0) return [];

    // Build list of path keys: ["03", "03/0303", "03/0303/030389"]
    const pathKeys = segs.map((_, i) => segs.slice(0, i + 1).join("/"));

    // Convert pathKey -> node (by matching path)
    return pathKeys
        .map((key) => {
            const node = options.find((o) => {
                const k = normalizePath(o.path).join("/");
                return k === key;
            });

            // fallback: sometimes parent nodes have no cnCode
            return {
                cnCode: node?.cnCode ?? segs[segs.length - 1],
                description: node?.description ?? "",
                key,
            };
        })
        .filter(Boolean);
}


function expandToSelected(
    options: CNCode[],
    selectedCnCode: string | null,
    setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>
) {
    if (!selectedCnCode) return;

    const selectedObj = options.find(o => o.cnCode === selectedCnCode);
    if (!selectedObj) return;

    const segs = normalizePath(selectedObj.path);
    if (segs.length === 0) return;

    // Add all parent path keys to expanded set so the selected node becomes visible
    setExpanded(prev => {
        const next = new Set(prev);
        for (let i = 1; i < segs.length; i++) {
            next.add(segs.slice(0, i).join("/"));
        }
        return next;
    });
}

function buildTree(codes: CNCode[]): TreeNode[] {
    const nodeMap: Record<string, TreeNode> = {};

    // Create nodes
    for (const code of codes) {
        const segments = normalizePath(code.path);
        const key = segments.join("/") || (code.cnCode ?? "");
        nodeMap[key] = {
            ...(code as any),
            cnCode: code.cnCode ?? null,
            children: [],
            _pathKey: key,
            allowedSelection: code.allowedSelection ?? true,
        };
    }

    // Assign children
    const roots: TreeNode[] = [];
    for (const node of Object.values(nodeMap)) {
        const segments = normalizePath(node.path);
        if (segments.length <= 1) {
            roots.push(node);
            continue;
        }
        const parentKey = segments.slice(0, -1).join("/");
        const parent = nodeMap[parentKey];
        if (parent) parent.children.push(node);
        else roots.push(node);
    }

    // stable sort
    const sortRec = (nodes: TreeNode[]) => {
        nodes.sort((a, b) => {
            const ac = a.cnCode ?? "";
            const bc = b.cnCode ?? "";
            if (ac !== bc) return ac.localeCompare(bc);
            return (a.description ?? "").localeCompare(b.description ?? "");
        });
        nodes.forEach((n) => sortRec(n.children));
    };
    sortRec(roots);

    return roots;
}

function tokenize(term: string) {
    return term
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .filter(Boolean);
}

function nodeText(node: TreeNode) {
    return `${node.cnCode ?? ""} ${node.description ?? ""}`.toLowerCase();
}

function useDebouncedValue<T>(value: T, delayMs: number) {
    const [debounced, setDebounced] = React.useState(value);
    React.useEffect(() => {
        const t = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(t);
    }, [value, delayMs]);
    return debounced;
}

function escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }: { text: string; query: string }) {
    const tokens = tokenize(query);
    if (tokens.length === 0) return <>{text}</>;

    const pattern = new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "ig");
    const parts = text.split(pattern);

    return (
        <>
            {parts.map((p, i) => {
                const isHit = tokens.some((t) => p.toLowerCase() === t.toLowerCase());
                return isHit ? (
                    <mark key={i} className="cncode-mark">
                        {p}
                    </mark>
                ) : (
                    <React.Fragment key={i}>{p}</React.Fragment>
                );
            })}
        </>
    );
}

// Filter tree + compute expand keys so matches are visible
function filterTreeWithAutoExpand(nodes: TreeNode[], term: string) {
    const tokens = tokenize(term);
    if (tokens.length === 0) {
        return { filtered: nodes, expandKeys: new Set<string>() };
    }

    const expandKeys = new Set<string>();

    function matchesTokens(text: string) {
        return tokens.every((t) => text.includes(t));
    }

    function filterNode(node: TreeNode, ancestorText: string): TreeNode | null {
        const self = nodeText(node);
        const combined = `${ancestorText} ${self}`.trim();

        const filteredChildren = node.children
            .map((c) => filterNode(c, combined))
            .filter(Boolean) as TreeNode[];

        const keep = matchesTokens(self) || matchesTokens(combined) || filteredChildren.length > 0;
        if (!keep) return null;

        if (filteredChildren.length > 0) expandKeys.add(node._pathKey);

        return { ...node, children: filteredChildren };
    }

    const filtered = nodes.map((n) => filterNode(n, "")).filter(Boolean) as TreeNode[];
    return { filtered, expandKeys };
}

function findSelectedNode(options: CNCode[], cnCode: string | null): CNCode | null {
    if (!cnCode) return null;
    return options.find((o) => o.cnCode === cnCode) ?? null;
}

// -------------------- component --------------------
const CNCodeTreeSelect: React.FC<CNCodeTreeSelectProps> = ({
    options,
    value,
    onChange,
    disabled = false,
    disabledText = "Veldu fisktegund til að velja CN kóða",
}) => {
    const treeData = React.useMemo(() => buildTree(options), [options]);

    const [selected, setSelected] = React.useState<string | null>(value ?? null);

    const breadcrumbs = React.useMemo(() => {
        return buildCnBreadcrumbs(options, selected);
    }, [options, selected]);

    // Collapsed by default. If nothing selected, still collapsed (shows "Velja" button).
    const [isOpen, setIsOpen] = React.useState(false);

    // tree state (only relevant when open)
    const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
    const [search, setSearch] = React.useState("");
    const debouncedSearch = useDebouncedValue(search, 120);

    // keep in sync with parent value
    React.useEffect(() => {
        setSelected(value ?? null);
        // if parent sets a value, collapse to show only selected
        if (value) setIsOpen(false);
    }, [value]);

    const selectedObj = React.useMemo(() => {
        const found = findSelectedNode(options, selected);
        if (found) return found;

        // Fallback: if selected exists but isn't in options (e.g. fish type filter), still show it
        if (selected) {
            return {
                cnCode: selected,
                description: "",
            } as CNCode;
        }

        return null;
    }, [options, selected]);

    const hasSelectableCodes = React.useMemo(() => {
        return options.some(o => !!o.cnCode && o.allowedSelection === true);
    }, [options]);

    const selectionDisabled = !hasSelectableCodes;

    const { filtered, expandKeys } = React.useMemo(
        () => filterTreeWithAutoExpand(treeData, debouncedSearch),
        [treeData, debouncedSearch]
    );

    // auto-expand during search
    React.useEffect(() => {
        if (!isOpen) return;
        if (!debouncedSearch.trim()) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            expandKeys.forEach((k) => next.add(k));
            return next;
        });
    }, [isOpen, debouncedSearch, expandKeys]);

    const toggleExpanded = React.useCallback((key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const selectNode = React.useCallback(
        (node: TreeNode) => {
            if (disabled) return;
            // Only allow selection when node is allowed
            if (node.allowedSelection === false) return;

            const id = node.cnCode ?? null;
            setSelected(id);
            onChange?.(id, node.description);

            // Collapse after select and reset search
            setIsOpen(false);
            setSearch("");
        },
        [onChange, disabled]
    );

    const clearSelection = React.useCallback(() => {
        setSelected(null);
        onChange?.(null, "");
        // stay collapsed; user can click "Velja" again
        setIsOpen(false);
        setSearch("");
    }, [onChange]);

    const openPicker = React.useCallback(() => {
        if (disabled) return;

        setIsOpen(true);
        setSearch("");

        // Expand to the currently selected node so it becomes visible
        expandToSelected(options, selected, setExpanded);

        // Scroll to the selected node once DOM renders
        setTimeout(() => {
            const el = treeRef.current?.querySelector<HTMLElement>(`[data-cncode="${selected}"]`);
            el?.scrollIntoView({ block: "center" });
            el?.focus();
        }, 50);
    }, [disabled, options, selected]);

    const onKeyDownWhenOpen = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") {
            e.preventDefault();
            setIsOpen(false);
            setSearch("");
        }
    };

    // Flatten for keyboard navigation (visible nodes only)
    const flat = React.useMemo(() => {
        if (!isOpen) return [];
        const out: TreeNode[] = [];
        const walk = (nodes: TreeNode[]) => {
            for (const n of nodes) {
                out.push(n);
                if (n.children.length > 0 && expanded.has(n._pathKey)) walk(n.children);
            }
        };
        walk(filtered);
        return out;
    }, [isOpen, filtered, expanded]);

    const treeRef = React.useRef<HTMLDivElement | null>(null);

    const focusRowByIndex = (idx: number) => {
        const el = treeRef.current?.querySelector<HTMLElement>(`[data-flat-idx="${idx}"]`);
        el?.focus();
        el?.scrollIntoView({ block: "nearest" });
    };

    const onTreeKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) return;

        const current = document.activeElement?.getAttribute("data-flat-idx");
        const idx = current ? Number(current) : -1;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = Math.min((idx < 0 ? 0 : idx + 1), flat.length - 1);
            focusRowByIndex(next);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            const next = Math.max((idx < 0 ? 0 : idx - 1), 0);
            focusRowByIndex(next);
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const node = flat[idx];
            if (node) selectNode(node);
        } else if (e.key === "ArrowRight") {
            const node = flat[idx];
            if (node && node.children.length > 0 && !expanded.has(node._pathKey)) {
                e.preventDefault();
                toggleExpanded(node._pathKey);
            }
        } else if (e.key === "ArrowLeft") {
            const node = flat[idx];
            if (node && expanded.has(node._pathKey)) {
                e.preventDefault();
                toggleExpanded(node._pathKey);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setIsOpen(false);
            setSearch("");
        }
    };

    // -------------------- collapsed view --------------------
    if (!isOpen) {

        // Disabled and nothing selected
        if (disabled) {
            return (
                <div className="cncode-collapsed">
                    <div className="cncode-empty-bar disabled">
                        <div className="cncode-empty-text">{disabledText}</div>
                        <button type="button" className="cncode-btn primary" disabled>
                            Velja CN kóða
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="cncode-collapsed">
                {selected ? (
                    <div>
                        {/* Breadcrumbs (secondary, compact) */}
                        {breadcrumbs.length > 0 && (
                            <div className="cncode-path-onepill">
                                <span
                                    className="cncode-path-pill"
                                    tabIndex={0}
                                >
                                    <span className="cncode-path-pill-label">Valinn CN kóði</span>
                                    <span className="cncode-path-pill-value" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <span>
                                            <span className="cncode-path-pill-code">
                                                {breadcrumbs[breadcrumbs.length - 1]?.cnCode ?? selectedObj?.cnCode ?? selected}
                                            </span>
                                            <span className="cncode-path-pill-sep"> – </span>
                                            <span className="cncode-path-pill-desc">
                                                {breadcrumbs[breadcrumbs.length - 1]?.description ?? selectedObj?.description ?? ""}
                                            </span>
                                        </span>

                                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div className="cncode-selected-actions">
                                                <button type="button" className="cncode-btn" onClick={openPicker}>
                                                    Breyta
                                                </button>
                                            </div>
                                            <div className="cncode-selected-actions">
                                                <button type="button" className="cncode-btn" onClick={clearSelection}>
                                                    Hreinsa
                                                </button>
                                            </div>
                                        </span>
                                    </span>

                                    {/* Hover / focus popover */}
                                    <span className="cncode-path-popover" role="tooltip">
                                        <div className="cncode-path-popover-title">Full slóð</div>

                                        <div className="cncode-path-popover-list">
                                            {breadcrumbs.map((b, idx) => (
                                                <div key={b.key} className="cncode-path-row">
                                                    <div className="cncode-path-step">
                                                        <span className="cncode-path-index">{idx + 1}</span>
                                                        <span className="cncode-path-code">{b.cnCode}</span>
                                                    </div>
                                                    {b.description ? (
                                                        <div className="cncode-path-desc">{b.description}</div>
                                                    ) : null}
                                                </div>
                                            ))}
                                        </div>
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="cncode-empty-bar">
                        <div className="cncode-empty-text">Enginn CN kóði valinn</div>
                        <button type="button" className="cncode-btn primary" onClick={openPicker}>
                            Velja CN kóða
                        </button>
                    </div>
                )
                }
            </div >
        );
    }

    // -------------------- open tree view --------------------
    let globalFlatIndex = -1;

    const renderNode = (node: TreeNode, depth = 0): React.ReactNode => {
        const key = node._pathKey;
        const hasChildren = node.children.length > 0;
        const isExpanded = expanded.has(key);
        const isSelected = (node.cnCode ?? null) === selected;

        globalFlatIndex += 1;
        const myFlatIndex = globalFlatIndex;

        return (
            <div key={key} className="cncode-tree-node" style={{ marginLeft: depth * 16 }}>
                <div
                    role="treeitem"
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    aria-selected={isSelected}
                    tabIndex={0}
                    data-flat-idx={myFlatIndex}
                    data-cncode={node.cnCode ?? ""}
                    className={[
                        "cncode-tree-row",
                        node.allowedSelection === false ? "disabled" : "",
                        isSelected ? "selected" : "",
                    ].join(" ")}
                    onKeyDown={onTreeKeyDown}
                    onClick={() => selectNode(node)}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            className="cncode-toggle"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(key);
                            }}
                        >
                            {isExpanded ? "▼" : "▶"}
                        </button>
                    ) : (
                        <span className="cncode-toggle-spacer" />
                    )}

                    <div className="cncode-text">
                        <span className="cncode-code">
                            <Highlight text={node.cnCode ?? ""} query={debouncedSearch} />
                        </span>
                        {node.cnCode ? <span className="cncode-sep">–</span> : null}
                        <span className="cncode-desc">
                            <Highlight text={node.description ?? ""} query={debouncedSearch} />
                        </span>
                    </div>
                </div>

                {hasChildren && isExpanded && (
                    <div role="group" className="cncode-children">
                        {node.children.map((c) => renderNode(c, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="cncode-open" onKeyDown={onKeyDownWhenOpen}>
            <div className="cncode-open-header">
                <div className="cncode-open-title">
                    Veldu CN kóða
                    <a
                        href="https://www.tariffnumber.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Smellið hér fyrir frekari upplýsingar um CN kóða"
                        title="Smellið hér fyrir frekari upplýsingar um CN kóða"
                        style={{
                            marginLeft: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "1px solid #cbd5e1",
                            color: "#64748b",
                            textDecoration: "none",
                            fontSize: 12,
                            lineHeight: 1,
                        }}
                    >
                        ?
                    </a>
                    {selectedObj?.cnCode ? (
                        <span className="cncode-open-current">
                            Núverandi: <strong>{selectedObj.cnCode}</strong>
                        </span>
                    ) : null}
                </div>

                <div className="cncode-open-actions">
                    {selectedObj?.cnCode ? (
                        <button type="button" className="cncode-btn" onClick={clearSelection}>
                            Hreinsa
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="cncode-btn"
                        onClick={() => {
                            setIsOpen(false);
                            setSearch("");
                        }}
                    >
                        Loka
                    </button>
                </div>
            </div>

            <div className="cncode-searchbar">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Leita (t.d. 0303)..."
                    className="cncode-search"
                />
                {search.trim() && (
                    <button
                        type="button"
                        className="cncode-clear"
                        onClick={() => setSearch("")}
                        aria-label="Hreinsa leit"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div ref={treeRef} role="tree" className="cncode-tree-select" aria-label="CN kóðar">
                {filtered.length === 0 ? (
                    <div className="cncode-empty">Engin niðurstaða</div>
                ) : (
                    filtered.map((n) => renderNode(n))
                )}
            </div>
        </div>
    );
};

export default CNCodeTreeSelect;
